import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, ProgressBar, Badge, Alert, Card, Spinner, Stack } from 'react-bootstrap';
import CampaignDetails from './campaigndetails';
import axios from 'axios';

function VoiceCampaignSummaryPage({ isLoggedIn, loggedInUserId }) {
    const { campaignid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [campaign, setCampaign] = useState(null);
    const [triggerInterestedFilter, setTriggerInterestedFilter] = useState(0);
    const callRecordsRef = React.useRef(null);

    useEffect(() => {
        if (!isLoggedIn || !loggedInUserId || loggedInUserId === '1' || loggedInUserId === 'null' || loggedInUserId === 'undefined') {
            navigate('/signin', { state: { from: location.pathname + location.search } });
            return;
        }

        axios.get(`/voicecampaigns/${loggedInUserId}`)
            .then(result => {
                const rows = result.data.map(row => ({
                    ...row,
                    campaigncompleted: parseInt(row.processed) === parseInt(row.totalnumbers),
                }));
                const campaigndetail = rows.find(c => String(c.id) === String(campaignid));
                if (!campaigndetail) {
                    setLoading(false);
                    alert("Campaign not found or you do not have access to this campaign.");
                    navigate('/campaigns/voice-campaigns');
                } else {
                    setCampaign(campaigndetail);
                }
            })
            .catch((err) => {
                console.log("error in fetching campaign data : ", err)
            })
            .finally(() => {
                setLoading(false);
            });
    }, [campaignid, isLoggedIn, loggedInUserId]);

    const calcProgress = (processed, total) => {
        if (!total) return 100;
        return Math.round((processed / total) * 100);
    };

    const formatDate = (dt) => {
        if (!dt) return '-';
        return new Date(dt).toLocaleString();
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    const progress = calcProgress(campaign.processed, campaign.totalnumbers);
    const isCompleted = campaign.campaigncompleted;
    const isPaused = campaign.ispaused === 1;
    const statusLabel = isCompleted ? 'Completed' : isPaused ? 'Paused' : 'In Progress';
    const headerBg = isCompleted ? 'success' : isPaused ? 'warning' : 'primary';

    return (
        <div className="bg-body-tertiary min-vh-100 d-flex flex-column pt-4 px-3 px-md-5">

            <div className="mx-auto w-100">
                <div className="">
                    {/* Header Area */}
                    <div className="p-4 text-white d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 bg-primary rounded-3">
                        <div>
                            <h3 className="mb-1 fw-bold d-flex align-items-center">
                                Campaign #{campaign.id}
                            </h3>
                            {campaign.name && (
                                <div className="opacity-75 fs-6 ms-1 fw-medium" style={{ letterSpacing: '0.5px' }}>
                                    {campaign.name}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card-body p-4 p-lg-5">
                        <Row className="g-5">
                            {/* Left details */}
                            <Col lg={6}>
                                <h6 className="text-muted fw-bold text-uppercase mb-4 d-flex align-items-center" style={{ letterSpacing: '1.5px', fontSize: '0.85rem' }}>
                                    <i className="bi bi-info-circle me-2 fs-5 text-primary"></i>
                                    Campaign Details
                                </h6>
                                <Stack gap={4}>
                                    {campaign.entity && (
                                        <div className="d-flex align-items-center">
                                            <div className="bg-primary bg-opacity-10 p-3 rounded-4 me-3 text-primary d-flex align-items-center justify-content-center shadow-sm" style={{ width: '50px', height: '50px' }}>
                                                <i className="bi bi-building fs-5"></i>
                                            </div>
                                            <div>
                                                <small className="text-secondary text-uppercase fw-semibold d-block mb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Entity</small>
                                                <div className="fs-5 fw-medium text-dark">{campaign.entity}</div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="d-flex align-items-center">
                                        <div className="bg-info bg-opacity-10 p-3 rounded-4 me-3 text-info d-flex align-items-center justify-content-center shadow-sm" style={{ width: '50px', height: '50px' }}>
                                            <i className="bi bi-person-badge fs-5"></i>
                                        </div>
                                        <div>
                                            <small className="text-secondary text-uppercase fw-semibold d-block mb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Started By</small>
                                            <div className="fs-5 fw-medium text-dark">{campaign.startedby || "-"}</div>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-warning bg-opacity-10 p-3 rounded-4 me-3 text-warning d-flex align-items-center justify-content-center shadow-sm" style={{ width: '50px', height: '50px' }}>
                                            <i className="bi bi-list-ul fs-5"></i>
                                        </div>
                                        <div>
                                            <small className="text-secondary text-uppercase fw-semibold d-block mb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Leads List</small>
                                            <div className="fs-5 fw-medium text-dark">{campaign.listname || "-"}</div>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-success bg-opacity-10 p-3 rounded-4 me-3 text-success d-flex align-items-center justify-content-center shadow-sm" style={{ width: '50px', height: '50px' }}>
                                            <i className="bi bi-calendar-play fs-5"></i>
                                        </div>
                                        <div>
                                            <small className="text-secondary text-uppercase fw-semibold d-block mb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Start Time</small>
                                            <div className="fs-5 fw-medium text-dark">{formatDate(campaign.starttime)}</div>
                                        </div>
                                    </div>
                                    {isCompleted && (
                                        <div className="d-flex align-items-center">
                                            <div className="bg-danger bg-opacity-10 p-3 rounded-4 me-3 text-danger d-flex align-items-center justify-content-center shadow-sm" style={{ width: '50px', height: '50px' }}>
                                                <i className="bi bi-calendar-check fs-5"></i>
                                            </div>
                                            <div>
                                                <small className="text-secondary text-uppercase fw-semibold d-block mb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>End Time</small>
                                                <div className="fs-5 fw-medium text-dark">{formatDate(campaign.endtime)}</div>
                                            </div>
                                        </div>
                                    )}
                                </Stack>
                            </Col>

                            {/* Right summary */}
                            <Col lg={6}>
                                <h6 className="text-muted fw-bold text-uppercase mb-4 d-flex align-items-center" style={{ letterSpacing: '1.5px', fontSize: '0.85rem' }}>
                                    <i className="bi bi-bar-chart-fill me-2 fs-5 text-primary"></i>
                                    Performance Overview
                                </h6>
                                
                                <Row className="g-3 mb-5">
                                    <Col xs={12} sm={4}>
                                        <div 
                                            className="border border-primary border-opacity-25 rounded-4 p-4 text-center bg-primary bg-opacity-10 text-primary hover-lift shadow-sm h-100 d-flex flex-column justify-content-center"
                                            style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                            onClick={() => {
                                                setTriggerInterestedFilter(Date.now());
                                                if (callRecordsRef.current) {
                                                    callRecordsRef.current.scrollIntoView({ behavior: 'smooth' });
                                                }
                                            }}
                                        >
                                            <div className="display-6 fw-bold lh-1 mb-1 text-primary">{campaign.interested ?? 0}</div>
                                            <small className="text-uppercase fw-bold mt-2 text-primary" style={{ fontSize: '0.8rem', letterSpacing: '1px' }}>Interested</small>
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={4}>
                                        <div className="border border-success border-opacity-25 rounded-4 p-4 text-center bg-success bg-opacity-10 hover-lift shadow-sm h-100 d-flex flex-column justify-content-center">
                                            <div className="text-success display-6 fw-bold lh-1 mb-1">{campaign.success ?? 0}</div>
                                            <small className="text-success text-uppercase fw-bold mt-2" style={{ fontSize: '0.8rem', letterSpacing: '1px' }}>Success</small>
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={4}>
                                        <div className="border border-danger border-opacity-25 rounded-4 p-4 text-center bg-danger bg-opacity-10 hover-lift shadow-sm h-100 d-flex flex-column justify-content-center">
                                            <div className="text-danger display-6 fw-bold lh-1 mb-1">{campaign.dnds ?? 0}</div>
                                            <small className="text-danger text-uppercase fw-bold mt-2" style={{ fontSize: '0.8rem', letterSpacing: '1px' }}>DND / Failed</small>
                                        </div>
                                    </Col>
                                </Row>

                                <div className="bg-light rounded-3 p-4 border shadow-sm">
                                    <div className="d-flex justify-content-between mb-3 align-items-end">
                                        <div>
                                            <span className="fw-bold text-dark fs-5 mb-0 d-block">{statusLabel}</span>
                                            <span className="text-muted small fw-medium">
                                                {campaign.processed} of {campaign.totalnumbers} calls processed
                                            </span>
                                        </div>
                                        <Badge bg={headerBg} pill className="fs-6 px-3 py-2 shadow-sm">{progress}%</Badge>
                                    </div>
                                    <ProgressBar 
                                        now={progress} 
                                        variant={headerBg} 
                                        style={{ height: '16px', borderRadius: '10px' }} 
                                        animated={!isCompleted}
                                        className="shadow-sm bg-secondary bg-opacity-25"
                                    />
                                </div>
                            </Col>
                            <div ref={callRecordsRef} className='px-0 mx-0'>
                                <h4 className='fw-bold mt-3 mb-3 text-primary'>Call Records</h4>
                                <CampaignDetails crid={campaign.id} isRunning={!isCompleted && !isPaused} triggerInterestedFilter={triggerInterestedFilter} />
                            </div>
                        </Row>
                    </div>
                </div>
                
                
            </div>
        </div>
    );
}

export default VoiceCampaignSummaryPage;


